import { useEffect, useMemo, useState } from "react";
import {
	AutocompleteInput,
	Create,
	FormTab,
	ImageField,
	ImageInput,
	SelectInput,
	BooleanInput,
	TabbedForm,
	TextInput,
	TranslatableInputs,
	useCreate,
	useGetList,
	useNotify,
	useRedirect,
	useTranslate,
	RadioButtonGroupInput,
} from "react-admin";
import { getChoices } from "./shared";
import { aspectRatios } from "../../utils/constants";
import { CustomTransferList } from "../../customComponents/CustomTransferList";
import { CustomContentTypeArrayInput } from "../../customComponents/CustomContentTypeArrayInput";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { validatePlaylistData } from "./playlistValidate";
import {
	setIndexesForData,
	transformToFormData,
} from "../../utils/customFunctions";
import { CustomToolbar } from "../../customComponents/CustomToolbar";
import CustomChipsArrayInput from "../../customComponents/CustomChipsArrayInput";

export const PlaylistCreate = (props) => {
	const [backgroundPlaylistsArray, setBackgroundPlaylistsArray] = useState(
		[]
	);
	const [categoriesArray, setCategoriesArray] = useState([]);
	const [languagesArray, setLanguagesArray] = useState([]);
	const [languageData, setLanguageData] = useState([]);
	const [objectivesDataArray, setObjectivesDataArray] = useState([]);
	const [create] = useCreate("playlists");
	const translate = useTranslate();
	const redirect = useRedirect();
	const notify = useNotify();

	const { data: backgroundPlaylistsData } = useGetList(
		"background-playlists"
	);

	const { data: categoriesData } = useGetList("categories");

	const { data: languagesData } = useGetList("languages");

	const { data: objectivesData } = useGetList("objectives");

	const onSuccess = () => {
		notify(translate("playlists.create.success"), "info");
		redirect("/playlists");
	};

	const onFailure = (error) => {
		notify(error.message, "warning");
	};

	const defaultLocale = useMemo(
		() => userPreferences.defaultTranslationLanguage,
		[]
	);

	useEffect(() => {
		setLanguageData(getChoices(languagesData));
		setLanguagesArray(
			Object.values(languagesData).map((language) =>
				language.name.slice(0, 2).toLowerCase()
			)
		);
	}, [languagesData]);

	useEffect(() => {
		setCategoriesArray(
			Object.values(categoriesData).map(({ id, titles }) => ({
				id,
				name: titles[defaultLocale],
			}))
		);
	}, [categoriesData]);

	useEffect(() => {
		setBackgroundPlaylistsArray(
			Object.values(backgroundPlaylistsData).map(({ id, names }) => ({
				id,
				name: names[defaultLocale],
			}))
		);
	}, [backgroundPlaylistsData]);

	useEffect(() => {
		setObjectivesDataArray(
			Object.values(objectivesData).map(({ id, titles }) => ({
				id,
				name: titles[defaultLocale],
			}))
		);
	}, [objectivesData]);
	return (
		<Create {...props} title={translate("playlists.create.title")}>
			<TabbedForm
				submitOnEnter={false}
				toolbar={<CustomToolbar />}
				save={(data) => {
					const indexedDataToSend = setIndexesForData(data, [
						"modules",
						"objectives",
					]);
					const transformed = transformToFormData(indexedDataToSend);
					create(
						{ payload: { data: transformed } },
						{
							onSuccess,
							onFailure,
						}
					);
				}}
				validate={validatePlaylistData}
				syncWithLocation={false}
			>
				<FormTab label={translate("playlists.tabs.basicInfo.label")}>
					<TranslatableInputs
						locales={languagesArray}
						defaultLocale={defaultLocale}
					>
						<TextInput source="names" />
						<TextInput multiline source="descriptions" />
					</TranslatableInputs>

					<AutocompleteInput
						resettable
						source="backgroundPlaylistId"
						choices={backgroundPlaylistsArray}
					/>

					<SelectInput
						source="categoryId"
						choices={categoriesArray}
					/>
					<RadioButtonGroupInput
						source="languageId"
						choices={languageData}
						optionText={({ name }) =>
							`misc.names.languages.${name}`
						}
						optionValue="id"
					/>
					<BooleanInput defaultValue={false} source="comingSoon" />
				</FormTab>
				<FormTab label="Manage Images">
					<ImageInput
						source="homePageImage"
						accept="image/*"
						helperText={
							"The aspect ratio should be " +
							aspectRatios.playlistInHomePage
						}
						placeholder={translate(
							"playlists.edit.uploadHomeImagePlaceholder"
						)}
					>
						<ImageField source="src" />
					</ImageInput>

					<ImageInput
						source="playlistPageImage"
						accept="image/*"
						helperText={
							"The aspect ratio should be " +
							aspectRatios.playlistInPlaylistPage
						}
						placeholder={translate(
							"playlists.create.uploadPlaylistImagePlaceholder"
						)}
					>
						<ImageField source="src" />
					</ImageInput>
				</FormTab>
				<FormTab
					label={translate("playlists.tabs.modulesManagement.label")}
				>
					<CustomTransferList
						source="modules"
						referenceSource="modules"
						referenceResource="modules"
						referenceField="name"
						isReferenceTranslatable={false}
					/>
				</FormTab>
				<FormTab
					label={translate(
						"playlists.tabs.objectivesManagement.label"
					)}
				>
					<CustomTransferList
						source="objectives"
						referenceSource="objectives"
						referenceResource="objectives"
						referenceField="titles"
						isReferenceTranslatable={true}
					/>
				</FormTab>
				<FormTab
					label={translate("playlists.tabs.typesManagement.label")}
				>
					<CustomContentTypeArrayInput />
				</FormTab>
			</TabbedForm>
		</Create>
	);
};
